/* ==========================================================================
   Shared a11y helpers — focus trap for modals/drawers + dialog wiring.
   Used by: quote-modal, catalog filter drawer, cookie banner.

   Why: when a dialog opens, Tab/Shift+Tab must stay inside it. Escape
   must close it. Focus must return to the trigger element on close.
   This is the WCAG 2.1.2 / 2.4.3 / 4.1.2 baseline.
   ========================================================================== */

(function () {
    const FOCUSABLE = [
        'a[href]:not([disabled])',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"]):not([disabled])',
        '[contenteditable="true"]',
    ].join(',');

    function getFocusable(root) {
        if (!root) return [];
        return Array.from(root.querySelectorAll(FOCUSABLE))
            .filter(el => !el.hasAttribute('hidden')
                       && el.offsetParent !== null
                       && !el.closest('[hidden]'));
    }

    // Active trap state — set when a dialog opens, cleared on close.
    let activeTrap = null;

    function onKeydown(e) {
        if (!activeTrap) return;
        if (e.key === 'Escape') {
            e.preventDefault();
            if (typeof activeTrap.onEscape === 'function') activeTrap.onEscape();
            return;
        }
        if (e.key !== 'Tab') return;
        const items = getFocusable(activeTrap.root);
        if (items.length === 0) {
            e.preventDefault();
            activeTrap.root.focus();
            return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
            if (active === first || !activeTrap.root.contains(active)) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (active === last || !activeTrap.root.contains(active)) {
                e.preventDefault();
                first.focus();
            }
        }
    }

    // Public API: install/uninstall a trap on a given root element.
    // restoreEl: focus returns here on close (typically the trigger button).
    window.VidaliteA11y = {
        trapFocus(root, opts = {}) {
            if (!root) return () => {};
            const previouslyFocused = document.activeElement;
            // Ensure root can receive focus for screen readers
            if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
            activeTrap = { root, onEscape: opts.onEscape };
            document.addEventListener('keydown', onKeydown, true);

            // Move focus into the dialog. Prefer an explicit autofocus target,
            // then first focusable, else the root itself.
            requestAnimationFrame(() => {
                const auto = root.querySelector('[autofocus]');
                const first = getFocusable(root)[0];
                (auto || first || root).focus();
            });

            // Return a release fn for the caller to invoke on close.
            return function release() {
                document.removeEventListener('keydown', onKeydown, true);
                activeTrap = null;
                const restoreEl = opts.restoreEl || previouslyFocused;
                if (restoreEl && typeof restoreEl.focus === 'function') {
                    try { restoreEl.focus(); } catch (_) {}
                }
            };
        },
    };
})();
